/**
 * Product Card Cart Toggle
 *
 * Manages the visibility of add-to-cart button vs quantity selector on product cards
 * based on whether the product is in the cart. Uses theme's default add to cart
 * and cart change APIs without modifying existing JS files.
 */
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from "@theme/events";
import { fetchConfig } from "@theme/utilities";

const SELECTOR = {
  toggle: "[data-product-card-cart-toggle]",
  quantityWrapper: "[data-product-card-quantity-wrapper]",
  addToCartWrapper: "[data-product-card-add-to-cart-wrapper]",
  quantitySelector: "quantity-selector-component",
};

let isInitialized = false;
let syncRafId = null;
let layoutRafId = null;
let deferredSyncTimeoutId = null;
let toggleResizeObserver = null;
let toggleMutationObserver = null;
const PRODUCT_RECOMMENDATIONS_LOADED_EVENT = "product-recommendations:loaded";

/**
 * Gets cart section IDs for cart change/update requests
 * @returns {string[]}
 */
function getCartSectionIds() {
  const ids = new Set();
  document
    .querySelectorAll("cart-items-component[data-section-id]")
    .forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });
  return Array.from(ids);
}

/**
 * Fetches the current cart
 * @returns {Promise<{items: Array<{variant_id: number, quantity: number}>}>}
 */
async function fetchCart() {
  const response = await fetch("/cart.js");
  return response.json();
}

/**
 * Finds the cart line index (1-based) for a variant ID
 * @param {Object} cart
 * @param {string} variantId
 * @returns {number|null}
 */
function getLineForVariant(cart, variantId) {
  if (!cart?.items?.length) return null;
  const index = cart.items.findIndex(
    (item) => item.variant_id.toString() === variantId.toString(),
  );
  return index >= 0 ? index + 1 : null;
}

/**
 * Finds the cart line index (1-based) for a cart item key.
 * @param {Object} cart
 * @param {string} key
 * @returns {number|null}
 */
function getLineForCartItemKey(cart, key) {
  if (!cart?.items?.length || !key) return null;
  const index = cart.items.findIndex((item) => String(item?.key || "") === key);
  return index >= 0 ? index + 1 : null;
}

/**
 * Gets cart quantity for a variant
 * @param {Object} cart
 * @param {string} variantId
 * @returns {number}
 */
function getCartQuantityForVariant(cart, variantId) {
  return (cart?.items || []).reduce((total, item) => {
    if (item?.variant_id?.toString() !== variantId.toString()) {
      return total;
    }

    return total + Number(item.quantity || 0);
  }, 0);
}

/**
 * Gets all cart items for a variant.
 * @param {Object} cart
 * @param {string} variantId
 * @returns {Array<any>}
 */
function getCartItemsForVariant(cart, variantId) {
  return Array.isArray(cart?.items)
    ? cart.items.filter(
        (item) => item?.variant_id?.toString() === variantId.toString(),
      )
    : [];
}

/**
 * Gets a normalized selling plan id from a cart item.
 * @param {any} item
 * @returns {string}
 */
function getCartItemSellingPlanId(item) {
  return String(
    item?.selling_plan_allocation?.selling_plan?.id ||
      item?.selling_plan_allocation?.selling_plan_id ||
      item?.selling_plan_allocation?.id ||
      "",
  ).trim();
}

/**
 * Gets the subscribed selling plan id for a variant already in cart.
 * Prefers actual subscription items and ignores the tagged free gift.
 * @param {Object} cart
 * @param {string} variantId
 * @returns {string}
 */
function getSubscribedSellingPlanIdForVariant(cart, variantId) {
  const item = cart?.items?.find(
    (i) =>
      i.variant_id.toString() === variantId.toString() &&
      getCartItemSellingPlanId(i) &&
      i?.properties?._gc_subscription_free_gift !== "true",
  );

  return item ? getCartItemSellingPlanId(item) : "";
}

/**
 * Returns the current selected selling plan id for a toggle.
 * Empty string means one-time.
 * @param {HTMLElement} toggle
 * @returns {string}
 */
function getSelectedSellingPlanIdForToggle(toggle) {
  const select = toggle.querySelector(
    "[data-product-card-selling-plan], [data-product-selling-plan]",
  );

  return select instanceof HTMLSelectElement ? String(select.value || "").trim() : "";
}

/**
 * Syncs the PLP selling-plan selector with the matching cart line.
 * @param {HTMLElement} toggle
 * @param {string} sellingPlanId
 * @param {number} cartQuantity
 */
function syncSellingPlanSelection(toggle, sellingPlanId, cartQuantity) {
  const select = toggle.querySelector(
    "[data-product-card-selling-plan], [data-product-selling-plan]",
  );
  if (!(select instanceof HTMLSelectElement)) return;

  const options = Array.from(select.options);
  const currentValue = select.value;

  const defaultSubscriptionOption =
    options.find((option) => {
      if (!option.value) return false;
      const planName = String(
        option.dataset.sellingPlanName || option.textContent || "",
      )
        .trim()
        .toLowerCase();
      return planName.includes("2 week");
    }) ||
    options.find((option) => option.value && option.dataset.oneTimeOption !== "true");

  let nextValue = currentValue;

  if (
    cartQuantity > 0 &&
    sellingPlanId &&
    options.some((option) => option.value === sellingPlanId)
  ) {
    nextValue = sellingPlanId;
  } else if (!currentValue) {
    nextValue = defaultSubscriptionOption?.value || currentValue;
  }

  if (!nextValue) return;
  if (!options.some((option) => option.value === nextValue)) return;
  if (select.value === nextValue) return;

  select.value = nextValue;
  select.dispatchEvent(new Event("change", { bubbles: true }));
}

/**
 * Returns true when the current card CTA is behaving as "Add to Box".
 * This covers one-time selections that are still routed through the
 * subscription/login flow and therefore must not flip into cart mode.
 * @param {HTMLElement} toggle
 * @returns {boolean}
 */
function isAddToBoxModeOnToggle(toggle) {
  const button = toggle.querySelector(
    '[data-product-card-add-to-cart-wrapper] button, [data-product-card-add-to-cart-wrapper] [ref="addToCartButton"]',
  );

  return (
    button instanceof HTMLButtonElement &&
    button.dataset.affinityPurchaseMode === "box"
  );
}

/**
 * Returns true when the card participates in the subscription / bundle flow.
 * @param {HTMLElement} toggle
 * @returns {boolean}
 */
function isSubscriptionAwareToggle(toggle) {
  return Boolean(
    toggle.querySelector(
      "[data-product-card-selling-plan-container], [data-product-card-selling-plan], [data-product-selling-plan]",
    ),
  );
}

/**
 * Returns true when the storefront currently knows the customer is logged in.
 * @returns {boolean}
 */
function isAffinityCustomerLoggedIn() {
  return (
    window.AffinityRechargeConfig?.customerLoggedIn === true ||
    Boolean(window.AffinityRechargeConfig?.customerId)
  );
}

/**
 * Returns the current variant id represented by a toggle.
 * @param {HTMLElement} toggle
 * @returns {string}
 */
function getToggleVariantId(toggle) {
  const variantIdInput = toggle.querySelector('input[name="id"]');
  return variantIdInput instanceof HTMLInputElement ? variantIdInput.value : "";
}

/**
 * Returns the current product id represented by a toggle.
 * @param {HTMLElement} toggle
 * @returns {string}
 */
function getToggleProductId(toggle) {
  const form = toggle.querySelector(".product-card-cart-toggle__form");
  return form instanceof HTMLElement ? form.dataset.productId || "" : "";
}

/**
 * Updates cart quantity via theme's cart change API
 * @param {number} line
 * @param {number} quantity
 * @returns {Promise<Object>}
 */
async function updateCartQuantity(line, quantity) {
  const sectionIds = getCartSectionIds();
  const body = JSON.stringify({
    line,
    quantity,
    sections: sectionIds.join(","),
    sections_url: window.location.pathname,
  });

  const response = await fetch(
    Theme.routes.cart_change_url,
    fetchConfig("json", { body }),
  );
  const text = await response.text();
  return JSON.parse(text);
}

/**
 * Updates multiple cart line quantities via cart update API.
 * @param {Record<string, number>} updates
 * @returns {Promise<Object>}
 */
async function updateCartQuantities(updates) {
  const sectionIds = getCartSectionIds();
  const body = JSON.stringify({
    updates,
    sections: sectionIds.join(","),
    sections_url: window.location.pathname,
  });

  const response = await fetch(
    Theme.routes.cart_update_url,
    fetchConfig("json", { body }),
  );
  const text = await response.text();
  return JSON.parse(text);
}

/**
 * Switches a product card to cart mode (show quantity, hide add-to-cart)
 * @param {HTMLElement} toggle
 * @param {number} cartQuantity
 */
function switchToCartMode(toggle, cartQuantity) {
  const quantityWrapper = toggle.querySelector(SELECTOR.quantityWrapper);
  const addToCartWrapper = toggle.querySelector(SELECTOR.addToCartWrapper);
  const quantitySelector = toggle.querySelector(SELECTOR.quantitySelector);
  const input = quantitySelector?.querySelector('input[name="quantity"]');

  if (quantityWrapper) quantityWrapper.classList.remove("hidden");
  if (addToCartWrapper) addToCartWrapper.classList.add("hidden");

  if (input && quantitySelector) {
    input.value = String(cartQuantity);
    input.setAttribute("data-cart-quantity", "0");
    input.min = "0";
    if (quantitySelector.updateCartQuantity) {
      quantitySelector.updateCartQuantity();
    }
    if (quantitySelector.updateButtonStates) {
      quantitySelector.updateButtonStates();
    }
  }

  toggle.dataset.cartMode = "true";
  updateToggleAddToCartWidth(toggle);
}

/**
 * Switches a product card to add mode (show add-to-cart, hide quantity)
 * @param {HTMLElement} toggle
 */
function switchToAddMode(toggle) {
  const quantityWrapper = toggle.querySelector(SELECTOR.quantityWrapper);
  const addToCartWrapper = toggle.querySelector(SELECTOR.addToCartWrapper);
  const quantitySelector = toggle.querySelector(SELECTOR.quantitySelector);
  const variantIdInput = toggle.querySelector('input[name="id"]');

  if (quantityWrapper) quantityWrapper.classList.add("hidden");
  if (addToCartWrapper) addToCartWrapper.classList.remove("hidden");

  if (quantitySelector && variantIdInput) {
    const quantityInput = quantitySelector.querySelector(
      'input[name="quantity"]',
    );
    const minQty = quantityInput?.getAttribute("data-min") || "1";
    if (quantityInput) {
      quantityInput.value = minQty;
      quantityInput.setAttribute("data-cart-quantity", "0");
      quantityInput.min = minQty;
      if (quantitySelector.updateCartQuantity) {
        quantitySelector.updateCartQuantity();
      }
      if (quantitySelector.updateButtonStates) {
        quantitySelector.updateButtonStates();
      }
    }
  }

  delete toggle.dataset.cartMode;
  updateToggleAddToCartWidth(toggle);
}

/**
 * Keeps add-to-cart width constrained when inline with price, and full-width when wrapped.
 * @param {HTMLElement} toggle
 */
function updateToggleAddToCartWidth(toggle) {
  const addToCartWrapper = toggle.querySelector(SELECTOR.addToCartWrapper);
  const quantityWrapper = toggle.querySelector(SELECTOR.quantityWrapper);
  const formButtons = toggle.querySelector(".product-form-buttons");
  const priceWrapper = toggle.querySelector(".product-card-price");
  const activeControlWrapper =
    (quantityWrapper instanceof HTMLElement &&
      !quantityWrapper.classList.contains("hidden") &&
      !quantityWrapper.hasAttribute("hidden") &&
      quantityWrapper) ||
    (addToCartWrapper instanceof HTMLElement &&
      !addToCartWrapper.classList.contains("hidden") &&
      !addToCartWrapper.hasAttribute("hidden") &&
      addToCartWrapper) ||
    null;
  const quantitySelector = quantityWrapper?.querySelector(
    "quantity-selector-component, cart-quantity-selector-component, .quantity-selector",
  );
  const quantityInput = quantitySelector?.querySelector('input[type="number"]');
  const quantityButtons = quantitySelector?.querySelectorAll('button');
  const form = toggle.querySelector(".product-card-cart-toggle__form");
  if (!(form instanceof HTMLElement)) return;

  const priceNode = toggle.querySelector('[ref="priceContainer"]');
  if (!(priceNode instanceof HTMLElement)) return;

  const priceRect = priceNode.getBoundingClientRect();
  const activeControlRect =
    activeControlWrapper instanceof HTMLElement
      ? activeControlWrapper.getBoundingClientRect()
      : null;
  const shouldStackControls = window.matchMedia("(max-width: 767px)").matches;
  const wrappedToNextLine =
    shouldStackControls ||
    (activeControlRect ? activeControlRect.top >= priceRect.bottom - 1 : false);

  if (formButtons instanceof HTMLElement) {
    formButtons.style.gridTemplateColumns = shouldStackControls
      ? "minmax(0, 1fr)"
      : "minmax(0, 1fr) minmax(6.5rem, 8rem)";
  }

  if (priceWrapper instanceof HTMLElement) {
    priceWrapper.style.gridColumn = shouldStackControls ? "1 / -1" : "1";
  }

  if (addToCartWrapper instanceof HTMLElement) {
    addToCartWrapper.style.gridColumn = shouldStackControls ? "1 / -1" : "2";
    addToCartWrapper.style.justifySelf = shouldStackControls
      ? "stretch"
      : "end";
    addToCartWrapper.style.width = "100%";
    addToCartWrapper.style.maxWidth = wrappedToNextLine ? "none" : "8rem";
    addToCartWrapper.style.marginLeft = wrappedToNextLine ? "0" : "auto";
  }

  if (quantityWrapper instanceof HTMLElement) {
    quantityWrapper.style.gridColumn = shouldStackControls ? "1 / -1" : "2";
    quantityWrapper.style.justifySelf = shouldStackControls
      ? "stretch"
      : "end";
    quantityWrapper.style.width = "100%";
    quantityWrapper.style.maxWidth = wrappedToNextLine ? "none" : "8rem";
    quantityWrapper.style.marginLeft = wrappedToNextLine ? "0" : "auto";
  }

  if (quantitySelector instanceof HTMLElement) {
    quantitySelector.style.width = "100%";
    quantitySelector.style.maxWidth = "100%";
    quantitySelector.style.display = "flex";
    quantitySelector.style.alignItems = "stretch";
    quantitySelector.style.justifyContent = "stretch";
  }

  if (quantityInput instanceof HTMLInputElement) {
    quantityInput.style.width = "0";
    quantityInput.style.maxWidth = "none";
    quantityInput.style.flex = "1 1 0";
    quantityInput.style.minWidth = "0";
  }

  if (quantityButtons) {
    quantityButtons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.style.width = "0";
      button.style.maxWidth = "none";
      button.style.flex = "1 1 0";
      button.style.minWidth = "0";
    });
  }
}

/**
 * Reconnects resize observers so width calculations re-run when card layout changes.
 */
function refreshToggleLayoutObserver() {
  if (typeof ResizeObserver === "undefined") return;

  if (toggleResizeObserver) {
    toggleResizeObserver.disconnect();
  }

  toggleResizeObserver = new ResizeObserver(() => {
    queueUpdateToggleAddToCartWidths();
  });

  document.querySelectorAll(SELECTOR.toggle).forEach((toggle) => {
    if (toggle instanceof HTMLElement) {
      toggleResizeObserver.observe(toggle);
    }
  });
}

/**
 * Returns true when a newly added node contains product-card cart toggles.
 * @param {Node} node
 * @returns {boolean}
 */
function nodeContainsToggle(node) {
  if (!(node instanceof HTMLElement)) return false;
  return node.matches(SELECTOR.toggle) || Boolean(node.querySelector(SELECTOR.toggle));
}

/**
 * Watches DOM additions (e.g. collection "Load more") and syncs newly inserted cards.
 */
function initToggleMutationObserver() {
  if (typeof MutationObserver === "undefined") return;
  if (!(document.body instanceof HTMLElement)) return;

  if (toggleMutationObserver) {
    toggleMutationObserver.disconnect();
  }

  toggleMutationObserver = new MutationObserver((mutations) => {
    let hasNewToggle = false;

    for (const mutation of mutations) {
      if (mutation.type !== "childList" || mutation.addedNodes.length === 0) {
        continue;
      }

      for (const node of mutation.addedNodes) {
        if (nodeContainsToggle(node)) {
          hasNewToggle = true;
          break;
        }
      }

      if (hasNewToggle) break;
    }

    if (!hasNewToggle) return;

    refreshToggleLayoutObserver();
    queueSyncProductCards();
    queueUpdateToggleAddToCartWidths();
  });

  toggleMutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

/**
 * Updates add-to-cart width behavior for all product card toggles.
 */
function updateAllToggleAddToCartWidths() {
  document.querySelectorAll(SELECTOR.toggle).forEach((toggle) => {
    if (toggle instanceof HTMLElement) {
      updateToggleAddToCartWidth(toggle);
    }
  });
}

/**
 * Queues width updates for the next animation frame.
 */
function queueUpdateToggleAddToCartWidths() {
  if (layoutRafId !== null) return;
  layoutRafId = requestAnimationFrame(() => {
    layoutRafId = null;
    updateAllToggleAddToCartWidths();
  });
}

/**
 * Syncs all product card buy buttons with current cart state
 * @param {Object} [cart] - Optional cart object; fetches if not provided
 */
async function syncProductCards(cart) {
  const toggles = document.querySelectorAll(SELECTOR.toggle);
  if (!toggles.length) return;

  const cartData = cart ?? (await fetchCart());

  toggles.forEach((toggle) => {
    const variantIdInput = toggle.querySelector('input[name="id"]');
    const variantId = variantIdInput?.value;
    if (!variantId) return;

    const cartQty = getCartQuantityForVariant(cartData, variantId);
    const sellingPlanId = getSubscribedSellingPlanIdForVariant(
      cartData,
      variantId,
    );

    syncSellingPlanSelection(toggle, sellingPlanId, cartQty);

    if (cartQty > 0) {
      switchToCartMode(toggle, cartQty);
    } else {
      switchToAddMode(toggle);
    }
  });

  refreshToggleLayoutObserver();
  queueUpdateToggleAddToCartWidths();
}

/**
 * Queues a sync on the next animation frame
 */
function queueSyncProductCards() {
  if (syncRafId !== null) return;

  syncRafId = requestAnimationFrame(async () => {
    syncRafId = null;
    try {
      await syncProductCards();
    } catch (error) {
      console.error(
        "Product card cart toggle: failed to sync product cards",
        error,
      );
    }

    queueUpdateToggleAddToCartWidths();
  });
}

/**
 * Defers a full cart sync so the clicked card can update immediately first.
 * @param {number} delay
 */
function queueDeferredSyncProductCards(delay = 450) {
  if (deferredSyncTimeoutId !== null) {
    window.clearTimeout(deferredSyncTimeoutId);
  }

  deferredSyncTimeoutId = window.setTimeout(() => {
    deferredSyncTimeoutId = null;
    queueSyncProductCards();
  }, delay);
}

/**
 * Handles quantity selector update from a product card in cart mode
 * @param {QuantitySelectorUpdateEvent} event
 */
async function handleQuantityUpdate(event) {
  const target = event.target;
  if (!(target instanceof Node)) return;

  const toggle = target.closest(SELECTOR.toggle);
  if (!toggle || toggle.dataset.cartMode !== "true") return;

  const variantIdInput = toggle.querySelector('input[name="id"]');
  const variantId = variantIdInput?.value;
  if (!variantId) return;

  const { quantity: newQuantity } = event.detail;
  if (typeof newQuantity !== "number" || newQuantity < 0) return;

  const cart = await fetchCart();
  const cartItems = getCartItemsForVariant(cart, variantId);
  if (!cartItems.length) return;
  const previousQuantity = getCartQuantityForVariant(cart, variantId);
  const selectedSellingPlanId = getSelectedSellingPlanIdForToggle(toggle);
  const preferredItem =
    cartItems.find((item) => {
      const itemSellingPlanId = getCartItemSellingPlanId(item);

      if (selectedSellingPlanId) {
        return itemSellingPlanId === selectedSellingPlanId;
      }

      return !itemSellingPlanId;
    }) || cartItems[0];

  try {
    let response;
    const quantityDelta = newQuantity - previousQuantity;

    if (quantityDelta === 0) {
      response = cart;
    } else if (quantityDelta > 0) {
      const preferredLine = getLineForCartItemKey(
        cart,
        String(preferredItem?.key || ""),
      );
      if (!preferredLine) return;

      response = await updateCartQuantity(
        preferredLine,
        Number(preferredItem.quantity || 0) + quantityDelta,
      );
    } else {
      let remainingReduction = Math.abs(quantityDelta);
      const prioritizedItems = [
        preferredItem,
        ...cartItems.filter((item) => item?.key !== preferredItem?.key),
      ];
      const updates = {};

      prioritizedItems.forEach((item) => {
        const currentItemQuantity = Number(item?.quantity || 0);
        if (!item?.key || remainingReduction <= 0 || currentItemQuantity <= 0) {
          return;
        }

        const nextItemQuantity = Math.max(currentItemQuantity - remainingReduction, 0);
        remainingReduction -= currentItemQuantity - nextItemQuantity;
        updates[item.key] = nextItemQuantity;
      });

      if (remainingReduction > 0) {
        return;
      }

      response = await updateCartQuantities(updates);
    }

    if (response?.status && response.status >= 400) {
      const input = toggle.querySelector(
        `${SELECTOR.quantitySelector} input[name="quantity"]`,
      );
      if (input) {
        input.value = String(previousQuantity);
      }
      return;
    }

    const resolvedQuantity = getCartQuantityForVariant(response, variantId);

    if (resolvedQuantity === 0) {
      switchToAddMode(toggle);
    } else {
      const input = toggle.querySelector(
        `${SELECTOR.quantitySelector} input[name="quantity"]`,
      );
      if (input) {
        input.value = String(resolvedQuantity);
        input.setAttribute("data-cart-quantity", "0");
        const qs = toggle.querySelector(SELECTOR.quantitySelector);
        if (qs?.updateButtonStates) qs.updateButtonStates();
      }
    }

    const itemCount = response?.item_count ?? 0;
    const sections = response?.sections ?? {};
    let changeType = "decrease";
    if (resolvedQuantity > previousQuantity) {
      changeType = "increase";
    } else if (resolvedQuantity === 0) {
      changeType = "remove";
    }

    const productForm = toggle.querySelector(".product-card-cart-toggle__form");
    const productId = productForm?.dataset.productId;
    const productTitle = toggle
      .closest(".resource-card, .product-grid__card, product-card")
      ?.querySelector("[ref='productTitleLink'], .resource-card__title")
      ?.textContent?.trim();

    document.dispatchEvent(
      new CartUpdateEvent(response, "product-card-cart-toggle", {
        itemCount,
        source: "product-card-cart-toggle",
        sections,
        productId,
        variantId,
        quantity: resolvedQuantity,
        previousQuantity,
        changeType,
        productTitle,
      }),
    );
  } catch (error) {
    console.error("Product card cart toggle: failed to update quantity", error);
    const input = toggle.querySelector(
      `${SELECTOR.quantitySelector} input[name="quantity"]`,
    );
    if (input) {
      const cartData = await fetchCart();
      const prevQty = getCartQuantityForVariant(cartData, variantId);
      input.value = String(prevQty);
    }
  }
}

/**
 * Handles add to cart success - switch matching product card to quantity mode
 * @param {CartAddEvent} event
 */
async function handleCartAdd(event) {
  const {
    source,
    productId,
    itemCount,
    didError,
    sellingPlanId,
    variantId: dataVariantId,
  } = event.detail?.data ?? {};
  if (source !== "product-form-component" || !productId) return;

  if (didError) {
    queueDeferredSyncProductCards(150);
    return;
  }

  const eventVariantId = dataVariantId || event.detail?.sourceId || "";
  const nextQuantity = Math.max(1, Number(itemCount) || 1);
  const toggles = Array.from(document.querySelectorAll(SELECTOR.toggle)).filter(
    (toggle) => {
      if (!(toggle instanceof HTMLElement)) return false;
      if (eventVariantId && getToggleVariantId(toggle) === String(eventVariantId)) {
        return true;
      }
      return getToggleProductId(toggle) === String(productId);
    },
  );

  if (toggles.length) {
    toggles.forEach((toggle) => {
      if (!(toggle instanceof HTMLElement)) return;
      switchToCartMode(toggle, nextQuantity);

      if (sellingPlanId) {
        syncSellingPlanSelection(toggle, String(sellingPlanId), nextQuantity);
      }
    });

    refreshToggleLayoutObserver();
    queueUpdateToggleAddToCartWidths();
  }

  queueDeferredSyncProductCards(sellingPlanId ? 250 : 700);
}

/**
 * Handles cart update from other sources (e.g. cart page) - sync product cards
 * Skips when source is product-form-component (handled by handleCartAdd) or product-card-cart-toggle
 * @param {CartUpdateEvent} event
 */
function handleCartUpdate(event) {
  const source = event.detail?.data?.source;
  if (
    source === "product-card-cart-toggle" ||
    source === "product-form-component"
  )
    return;
  const cart = event.detail?.resource;
  if (cart?.items?.length) {
    syncProductCards(cart);
  } else {
    syncProductCards();
  }
}

function init() {
  if (isInitialized) return;
  isInitialized = true;

  document.addEventListener(
    ThemeEvents.quantitySelectorUpdate,
    handleQuantityUpdate,
  );
  document.addEventListener(ThemeEvents.cartUpdate, handleCartAdd);
  document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
  document.addEventListener(
    PRODUCT_RECOMMENDATIONS_LOADED_EVENT,
    queueSyncProductCards,
  );
  window.addEventListener("resize", queueUpdateToggleAddToCartWidths);
  window.addEventListener("load", queueUpdateToggleAddToCartWidths);

  initToggleMutationObserver();
  refreshToggleLayoutObserver();
  queueSyncProductCards();
  queueUpdateToggleAddToCartWidths();

  // Recalculate once more after initial paint to avoid transient first-render widths.
  requestAnimationFrame(() => queueUpdateToggleAddToCartWidths());
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
